import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import type { MenuProps } from 'antd';

import { TIMEZONES } from '@/shared/constants/timezones';
import { useUserTimezone } from '@/shared/lib/hooks';
import { DropdownSelect } from '@/shared/ui/dropdown-select';

export interface TimezoneSelectorProps {
    onChange?: (timezone: string) => void;
    isDisabled?: boolean;
}

export const TimezoneSelector = memo(
    ({ onChange, isDisabled = false }: TimezoneSelectorProps) => {
        const [userTimezone, setUserTimezone] = useUserTimezone();

        // Local state to force update when the timezone changes
        const [currentTimezone, setCurrentTimezone] = useState(
            userTimezone || 'Etc/UTC',
        );

        // Update local state when userTimezone changes
        useEffect(() => {
            if (userTimezone) {
                setCurrentTimezone(userTimezone);
            }
        }, [userTimezone]);

        const menuItems: MenuProps['items'] = useMemo(
            () =>
                TIMEZONES.map((timezone) => ({
                    key: timezone.key,
                    label: `(${timezone.offset}) ${timezone.label}`,
                })),
            [],
        );

        const handleTimezoneChange = useCallback(
            (info: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
                const newTimezone = info.key;

                setUserTimezone(newTimezone);
                setCurrentTimezone(newTimezone);
                onChange?.(newTimezone);
            },
            [setUserTimezone, onChange],
        );

        // Effect to handle external changes to userTimezone
        useEffect(() => {
            if (onChange && userTimezone) {
                onChange(userTimezone);
            }
        }, [userTimezone, onChange]);

        return (
            <DropdownSelect
                key={currentTimezone} // Force re-render when timezone changes
                initialSelectedKey={currentTimezone}
                isDisabled={isDisabled}
                items={menuItems}
                onClick={handleTimezoneChange}
            />
        );
    },
);

TimezoneSelector.displayName = 'TimezoneSelector';
